import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertOctagon } from "lucide-react";

// Polls /api/symptom-checker/recent-emergencies every 30 seconds while the
// admin dashboard is open. On any NEW emergency (created_at > last-seen),
// fires a sticky sonner toast so the front desk can proactively reach out.
const POLL_INTERVAL_MS = 30_000;
const API = `${process.env.REACT_APP_BACKEND_URL}/api/symptom-checker`;
const LS_KEY = "sc_last_emergency_ts";

const EmergencyAlertPoller = () => {
  const timerRef = useRef(null);

  useEffect(() => {
    const initial = localStorage.getItem(LS_KEY) || new Date(Date.now() - 60_000).toISOString();
    let lastSeen = initial;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/recent-emergencies`, {
          params: { since: lastSeen },
          withCredentials: true,
        });
        const items = data?.emergencies || [];
        // Newest first — walk from oldest to newest so toasts appear in order.
        for (const e of [...items].reverse()) {
          toast(
            (
              <div>
                <div className="flex items-center gap-2 text-red-500 font-medium text-[13px] uppercase tracking-[0.14em]">
                  <AlertOctagon size={15} strokeWidth={2} /> Emergency triage
                </div>
                <div className="mt-1.5 font-medium text-[14px] text-[hsl(var(--foreground))]">
                  {e.summary || "New emergency assessment received."}
                </div>
                <div className="mt-1 text-[11.5px] text-[hsl(var(--foreground))]/70">
                  {e.age_band} · {e.eye_side} eye · {e.main_symptom}
                </div>
              </div>
            ),
            { duration: 15000, className: "border-red-500/40" }
          );
          if (e.created_at > lastSeen) lastSeen = e.created_at;
        }
        localStorage.setItem(LS_KEY, lastSeen);
      } catch {
        // silent — admin may not have poll permission or backend is down
      }
    };

    // Fire once immediately, then poll.
    tick();
    timerRef.current = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return null;
};

export default EmergencyAlertPoller;
